/**
 * Device preferences — how this phone renders, not who the player is. These
 * never touch the server: motion, colour and language are per-install choices,
 * while anything that limits play (cool-off, spend cap) is account state and
 * lives in ResponsibleLimits, where the server can enforce it.
 *
 * The attributes written here are the hooks stylesheets key off:
 *   :root[data-motion='reduced']  · physics flourishes become fades (§5.2)
 *   :root[data-cb='on']           · shape cues in addition to colour (§7.3)
 */

export type Lang = 'tl' | 'en';

/**
 * Which camera the table runs. `room` is the staged 2.5D table at 56°, `top`
 * is the flat overhead layout. Both render the same board markup — see
 * parts/room.css — so this is a genuine preference, not two implementations.
 */
export type TableView = 'room' | 'top';

/**
 * Where the player is sitting, when the camera is the seated one.
 *
 * Pan swings the chair round the table, tilt raises and lowers the eye, and
 * zoom leans in. They are device state like every other pref — one player likes
 * to be right down on the felt, another wants to see all four seats, and
 * neither is wrong.
 *
 * The clamps are not taste. Past them the table stops being playable: too flat
 * and a felt of cards is a line, too steep and it is the top view with extra
 * steps, too close and the far seat leaves the frame.
 */
export interface Camera {
  /** Degrees off the top view. 40 is a raked diorama, 82 is chin-on-the-table. */
  tilt: number;
  /** Degrees round the table. 0 is square to your own edge. */
  pan: number;
  /** How far you lean in. */
  zoom: number;
}

export const CAMERA_LIMITS = {
  tilt: { min: 42, max: 78, step: 1 },
  pan: { min: -40, max: 40, step: 1 },
  zoom: { min: 0.9, max: 1.6, step: 0.01 },
} as const;

export const DEFAULT_CAMERA: Camera = { tilt: 74, pan: 0, zoom: 1.3 };

/**
 * Four steps, not a slider. A slider on a phone in landscape is a 40px thumb
 * war for a number nobody remembers; four named levels are one tap each,
 * auditionable, and enough — the synth's whole dynamic range is "background"
 * to "clearly there".
 */
export type SfxLevel = 'off' | 'mahina' | 'katamtaman' | 'malakas';

const SFX_LEVELS: readonly SfxLevel[] = ['off', 'mahina', 'katamtaman', 'malakas'];

export interface LocalPrefs {
  lang: Lang;
  /** Table sounds, at a preset level. Katamtaman by default — it is a game. */
  sfx: SfxLevel;
  reducedMotion: boolean;
  colourblind: boolean;
  tableView: TableView;
  /** Only meaningful in the seated view; the top camera is a flat plan. */
  camera: Camera;
  /**
   * The last buy-in taken at each `game:tier`. A player who plays 2,000 at
   * Barya plays 2,000 the next night too, and re-dragging a slider from the
   * floor every single time is the kind of friction nobody reports and
   * everybody feels. Device state — the server re-validates the number anyway.
   */
  stakes: Record<string, number>;
}

const KEY = 'tpm.prefs';

export const DEFAULT_PREFS: LocalPrefs = {
  lang: 'tl',
  sfx: 'katamtaman',
  reducedMotion: false,
  colourblind: false,
  tableView: 'room',
  camera: DEFAULT_CAMERA,
  stakes: {},
};

export function readPrefs(): LocalPrefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<LocalPrefs>;
    return {
      lang: parsed.lang === 'en' ? 'en' : 'tl',
      sfx: readSfx(parsed),
      reducedMotion: parsed.reducedMotion === true,
      colourblind: parsed.colourblind === true,
      tableView: parsed.tableView === 'top' ? 'top' : 'room',
      camera: readCamera(parsed.camera),
      stakes: readStakes(parsed.stakes),
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

/**
 * The level, with both generations of installs behind it: a valid `sfx` wins;
 * before that the pref was a boolean called `sound`, so `sound: false` is a
 * player who already chose silence and keeps it. Everyone else gets the
 * default — absent must mean on, silence is chosen, never inherited.
 */
function readSfx(parsed: Partial<LocalPrefs> & { sound?: unknown }): SfxLevel {
  if (SFX_LEVELS.includes(parsed.sfx as SfxLevel)) return parsed.sfx as SfxLevel;
  if (parsed.sound === false) return 'off';
  return 'katamtaman';
}

/**
 * The camera, clamped on the way in. It is three numbers in localStorage that
 * feed a CSS transform, so a hand-edited file must not be able to fold the
 * table inside out — every value is pulled back into range rather than trusted.
 */
function readCamera(raw: unknown): Camera {
  if (typeof raw !== 'object' || raw === null) return DEFAULT_CAMERA;
  const r = raw as Partial<Record<keyof Camera, unknown>>;
  const axis = (k: keyof Camera): number => {
    const v = r[k];
    if (typeof v !== 'number' || !Number.isFinite(v)) return DEFAULT_CAMERA[k];
    const { min, max } = CAMERA_LIMITS[k];
    return Math.min(Math.max(v, min), max);
  };
  return { tilt: axis('tilt'), pan: axis('pan'), zoom: axis('zoom') };
}

/**
 * Remembered stakes, narrowed. This is the one pref carrying numbers rather
 * than a fixed set of strings, and it is read straight into a buy-in — so a
 * hand-edited or corrupted entry gets dropped rather than sent to the server.
 */
function readStakes(raw: unknown): Record<string, number> {
  if (typeof raw !== 'object' || raw === null) return {};
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'number' && Number.isInteger(value) && value > 0) out[key] = value;
  }
  return out;
}

/** The stake this device last took at a venue and tier, if it is still legal there. */
export function lastStake(game: string, tier: string, min: number, max: number): number {
  const seen = currentPrefs().stakes[`${game}:${tier}`];
  if (seen === undefined) return min;
  return Math.min(Math.max(seen, min), max);
}

export function rememberStake(game: string, tier: string, amount: number): void {
  const prefs = currentPrefs();
  savePrefs({ ...prefs, stakes: { ...prefs.stakes, [`${game}:${tier}`]: amount } });
}

export function applyPrefs(p: LocalPrefs): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.lang = p.lang === 'en' ? 'en' : 'fil';
  if (p.reducedMotion) root.setAttribute('data-motion', 'reduced');
  else root.removeAttribute('data-motion');
  if (p.colourblind) root.setAttribute('data-cb', 'on');
  else root.removeAttribute('data-cb');
}

export function savePrefs(p: LocalPrefs): void {
  current = p;
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    // Private mode, or a test with no DOM. The session still honours the
    // choice; only the remembering is lost.
  }
  applyPrefs(p);
  for (const fn of listeners) fn();
}

/* ── Subscription ────────────────────────────────────────────────────────── */

/*
 * Preferences are device state, not server state, so they stay out of the app
 * store. Screens that must re-render on a change (the table camera) subscribe
 * here instead; everything else keeps reading them once at mount.
 */

let current: LocalPrefs = readPrefs();
const listeners = new Set<() => void>();

export function currentPrefs(): LocalPrefs {
  return current;
}

export function subscribePrefs(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
